"use client";

import { cn } from "@oreme/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import React, {
  type HTMLAttributes,
  type RefAttributes,
  type ReactElement,
  type PropsWithChildren,
  useReducer,
} from "react";

const featureCardVariants = cva("relative flex flex-col overflow-hidden", {
  variants: {
    alignment: {
      none: "",
      vertical: "grid grid-rows-2",
      horizontal: "grid grid-cols-2",
    },
  },
  defaultVariants: {
    alignment: "none",
  },
});

const featureCardContentVariants = cva("flex flex-col gap-4 p-8", {
  variants: {
    position: {
      none: "",
      top: "self-start lg:w-1/2 lg:pt-12 lg:pl-12 before:from-black/70 to-black/0",
      bottom:
        "self-end lg:w-1/2 lg:pl-12 lg:pb-12 before:from-black/0 before:to-black/40",
      right: "ml-auto lg:pt-12 lg:pr-12",
      "bottom right": "self-end ml-auto lg:pr-12 lg:pb-12",
    },
  },
  defaultVariants: {
    position: "none",
  },
});

const featureCardImageVariants = cva(
  "absolute z-0 inset-0 flex justify-center items-center",
  {
    variants: {},
  },
);

const featureCardFooterVariants = cva(
  "flex flex-row items-center justify-between p-6",
  {
    variants: {},
  },
);

interface FeatureCardProps
  extends
    HTMLAttributes<HTMLDivElement>,
    RefAttributes<HTMLDivElement>,
    VariantProps<typeof featureCardVariants> {}

function FeatureCard({
  alignment,
  className,
  ref,
  ...props
}: Readonly<FeatureCardProps>): ReactElement {
  return (
    <div
      className={cn(featureCardVariants({ alignment }), className)}
      ref={ref}
      {...props}
    />
  );
}
FeatureCard.displayName = "FeatureCard";

interface FeatureCardOverlineProps
  extends HTMLAttributes<HTMLSpanElement>, PropsWithChildren {}

function FeatureCardOverline({
  className,
  children,
  ...props
}: Readonly<FeatureCardOverlineProps>): ReactElement {
  return (
    <div>
      <span className={cn("block uppercase", className)} {...props}>
        {children}
      </span>
    </div>
  );
}

FeatureCardOverline.displayName = "FeatureCardOverline";

interface FeatureCardTitleProps
  extends
    HTMLAttributes<HTMLDivElement>,
    RefAttributes<HTMLDivElement>,
    PropsWithChildren {}

function FeatureCardTitle({
  className,
  ref,
  children,
  ...props
}: Readonly<FeatureCardTitleProps>): ReactElement {
  return (
    <h3
      className={cn(
        "text-2xl leading-none lg:text-3xl lg:leading-none font-semibold",
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
    </h3>
  );
}
FeatureCardTitle.displayName = "FeatureCardTitle";

interface FeatureCardDescriptionProps
  extends HTMLAttributes<HTMLDivElement>, RefAttributes<HTMLDivElement> {}

function FeatureCardDescription({
  className,
  ...props
}: Readonly<FeatureCardDescriptionProps>): ReactElement {
  return <p className={cn("text-sm leading-tight", className)} {...props} />;
}
FeatureCardDescription.displayName = "FeatureCardDescription";

interface FeatureCardContentProps
  extends
    HTMLAttributes<HTMLDivElement>,
    RefAttributes<HTMLDivElement>,
    VariantProps<typeof featureCardContentVariants> {}

function FeatureCardContent({
  position,
  className,
  ...props
}: Readonly<FeatureCardContentProps>): ReactElement {
  return (
    <div
      className={cn(featureCardContentVariants({ position }), className)}
      {...props}
    />
  );
}
FeatureCardContent.displayName = "FeatureCardContent";

interface FeatureCardImageProps
  extends
    HTMLAttributes<HTMLDivElement>,
    PropsWithChildren,
    VariantProps<typeof featureCardImageVariants> {}

function FeatureCardImage({
  className,
  children,
  ...props
}: Readonly<FeatureCardImageProps>): ReactElement {
  return (
    <figure className={cn(featureCardImageVariants({}), className)} {...props}>
      {children}
    </figure>
  );
}
FeatureCardImage.displayName = "FeatureCardImage";

function FeatureCardFooter({
  className,
  children,
  ...props
}: Readonly<FeatureCardImageProps>): ReactElement {
  return (
    <footer className={cn(featureCardFooterVariants({}), className)} {...props}>
      {children}
    </footer>
  );
}
FeatureCardFooter.displayName = "FeatureCardFooter";

interface FeatureCardLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  href: string;
}

function FeatureCardLink({
  href,
  className,
  ...props
}: Readonly<FeatureCardLinkProps>): ReactElement {
  const [isHovered, toggleHovered] = useReducer((s) => {
    return !s;
  }, false);
  return (
    <Link
      className={cn("after:absolute after:inset-0", className)}
      href={href}
      onMouseEnter={() => toggleHovered()}
      onMouseLeave={() => toggleHovered()}
      {...props}
    >
      <span
        className={`absolute right-6 bottom-6 flex justify-center items-center w-10 h-10 rounded-full text-foreground/60 border bg-background animate__animated animate__faster ${isHovered ? "animate__rotateIn" : null}`}
      >
        <ArrowRight strokeWidth={1} />
      </span>
    </Link>
  );
}
FeatureCardLink.displayName = "FeatureCardLink";

export {
  FeatureCard,
  FeatureCardOverline,
  FeatureCardTitle,
  FeatureCardDescription,
  FeatureCardContent,
  FeatureCardImage,
  FeatureCardFooter,
  FeatureCardLink,
  type FeatureCardProps,
  type FeatureCardOverlineProps,
  type FeatureCardTitleProps,
  type FeatureCardDescriptionProps,
  type FeatureCardContentProps,
  type FeatureCardImageProps,
  type FeatureCardLinkProps,
};
